'use client'

import { useEffect, useState } from 'react'
import { isNativePlatform } from '@/lib/utils/platform'

/**
 * Whether the device is offline, and whether this is the native app. The
 * offline banners (classic and Encuentro) only show on native: web browsers
 * handle offline themselves, and `navigator.onLine` can false-positive there.
 *
 * Browser `online`/`offline` events everywhere; on native, also the Capacitor
 * Network plugin when it is installed.
 */
export function useNetworkStatus(): { isOffline: boolean; isNative: boolean } {
  const [isOffline, setIsOffline] = useState(false)
  const [isNative, setIsNative] = useState(false)

  useEffect(() => {
    setIsNative(isNativePlatform())

    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    // Use browser events for both web and native
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Check initial state
    if (!navigator.onLine) {
      setIsOffline(true)
    }

    // On native, also use Capacitor Network plugin if available. Not installed
    // today (ALU-383, needs Juan's approval), so this lands in .catch; kept
    // leak-free so adding the package is the only change left.
    let removeNative: (() => void) | undefined
    let cancelled = false
    if (isNativePlatform()) {
      // Dynamic import with type workaround — @capacitor/network types may not be installed
      import(/* webpackIgnore: true */ '@capacitor/network' as string)
        .then(
          async (mod: {
            Network: {
              addListener: (
                event: string,
                cb: (status: { connected: boolean }) => void
              ) => Promise<{ remove: () => Promise<void> | void }>
            }
          }) => {
            const handle = await mod.Network.addListener(
              'networkStatusChange',
              (status: { connected: boolean }) => setIsOffline(!status.connected)
            )
            if (cancelled) void handle.remove()
            else removeNative = () => void handle.remove()
          }
        )
        .catch(() => {
          // Plugin not available — browser events are fallback
        })
    }

    return () => {
      cancelled = true
      removeNative?.()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { isOffline, isNative }
}
